import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { FormHelperText, IconButton, Stack, Typography, styled as muiStyled } from "@mui/material";
import { DateCalendar, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { add, formatISO, set } from "date-fns";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetAppointmentsV1, usePostAppointmentV1 } from "api/services/appointment/hooks";
import type { PostAppointmentV1Payload } from "api/services/appointment/schemas/appointment_v1";
import type { Medium, Purpose } from "api/services/appointment/schemas/shared";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISelect } from "shared/atoms/MUI";
import { Notification } from "shared/atoms/Notification";
import { CloseIcon } from "shared/icons";
import { RED } from "theme/colors";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { reportError } from "utils/errorReporting";
import { useWindowSize } from "utils/hooks";

import type { BookedDayProps } from "./BookedDay";
import { BookedDay } from "./BookedDay";
import { DaySchedule } from "./DaySchedule";
import { DoubleBookingWarning } from "./DoubleBookingWarning";
import { getMinStartTime } from "./utils";

interface FormFields {
  date: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  purpose: Purpose | undefined;
  medium: Medium | undefined;
}

interface BookingFormV1Props {
  patientId: number;
  closeCallback: () => void;
}

export const BookingFormV1: React.FC<BookingFormV1Props> = ({ patientId, closeCallback }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { profile } = useProfileContext();
  const { isLargeScreen } = useWindowSize();
  const [error, setError] = useState<boolean>(false);
  const postAppointmentV1 = usePostAppointmentV1();

  invariant(profile, "Profile should be defined");

  const { data: appointmentsV1 = [] } = useGetAppointmentsV1(profile.id);

  const form = useForm<FormFields>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      date: null,
      startTime: null,
      endTime: null,
      purpose: (searchParams.get("meeting_purpose") as Purpose) || "",
      medium: (searchParams.get("meeting_medium") as Medium) || "",
    },
  });
  const watchDate = form.watch("date");
  const watchStartTime = form.watch("startTime");
  const watchEndTime = form.watch("endTime");

  // Automatically set endTime to startTime + 15 min whenever startTime changes
  useEffect(() => {
    if (watchStartTime) {
      form.setValue("endTime", add(watchStartTime, { minutes: 15 }), {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [watchStartTime]);

  const getPurposeOptions = useCallback(() => {
    const options = [
      { value: "kick_off", label: t("messages.appointments.types.KICK_OFF") },
      { value: "follow_up", label: t("messages.appointments.types.FOLLOW_UP") },
      { value: "general", label: t("messages.appointments.types.GENERAL") },
    ];

    if (profile?.market === "SE") {
      options.push({ value: "discharge_call", label: t("messages.appointments.types.DISCHARGE_CALL") });
    }

    return options;
  }, [profile?.market]);

  const getMediumOptions = useCallback(() => {
    const options = [{ value: "phone", label: t("messages.appointments.types.PHONE") }];

    if (profile?.therapist_profile?.video_calls_enabled || profile?.therapist_profile?.payer_video_call_required) {
      options.push({ value: "video", label: t("messages.appointments.types.VIDEO") });
    }

    return options;
  }, [profile?.therapist_profile]);

  const onSubmit = form.handleSubmit(async ({ date, startTime, endTime, purpose, medium }) => {
    if (!date || !startTime || !endTime || !purpose || !medium) return;

    try {
      const startDate = set(date, {
        hours: startTime.getHours(),
        minutes: startTime.getMinutes(),
        seconds: 0,
        milliseconds: 0,
      });
      const endDate = set(date, {
        hours: endTime.getHours(),
        minutes: endTime.getMinutes(),
        seconds: 0,
        milliseconds: 0,
      });

      const payload: PostAppointmentV1Payload = {
        health_care_professional_id: profile?.id,
        patient_id: patientId,
        start_time: formatISO(startDate),
        end_time: formatISO(endDate),
        purpose,
        medium,
        location: medium === "phone" ? null : "-",
      };

      await postAppointmentV1.mutateAsync(payload, {
        onSuccess(data) {
          AnalyticsService.track(AnalyticsEvents.MESSAGES.SCHEDULE_CALL, {
            purpose: data.purpose,
            patient: data.patient_id,
          });
          closeCallback();
        },
      });
    } catch (e) {
      setError(true);
      reportError("BookingFormV1 onSubmit error", e instanceof Error ? e : new Error("Unknown error"));
    }
  });

  return (
    <Container data-testid="booking-form-v1">
      <FormProvider {...form}>
        <form onSubmit={onSubmit} noValidate>
          <Stack spacing={3} py={3} px={2}>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h4">{t("messages.schedule_call")}</Typography>
              <IconButton onClick={closeCallback} data-testid="booking-form-v1-close">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Controller
              control={form.control}
              name="date"
              rules={{ required: true }}
              render={({ field, fieldState }) =>
                isLargeScreen ? (
                  <Stack
                    spacing={0.5}
                    sx={{
                      border: "1px solid",
                      borderColor: fieldState.error ? RED[100] : "transparent",
                      borderRadius: "8px",
                    }}
                  >
                    <StyledDateCalendar
                      value={field.value}
                      onChange={field.onChange}
                      disablePast
                      showDaysOutsideCurrentMonth
                      fixedWeekNumber={6}
                      slots={{ day: BookedDay }}
                      slotProps={{ day: { appointmentsV1 } as Partial<BookedDayProps> }}
                    />
                    {fieldState.error && (
                      <FormHelperText error sx={{ mx: "14px", my: "8px" }}>
                        {t("errors.string.empty")}
                      </FormHelperText>
                    )}
                  </Stack>
                ) : (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    disablePast
                    slots={{ day: BookedDay }}
                    slotProps={{
                      day: { appointmentsV1 } as Partial<BookedDayProps>,
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error ? t("errors.string.empty") : undefined,
                        fullWidth: true,
                      },
                    }}
                  />
                )
              }
            />

            {watchDate && <DaySchedule chosenDate={watchDate} appointmentsV1={appointmentsV1} />}

            <Stack spacing={2}>
              <Controller
                control={form.control}
                name="startTime"
                rules={{ required: true, validate: v => v !== null }}
                render={({ field, fieldState }) => (
                  <StyledTimePicker
                    label={t("common.begins")}
                    value={field.value}
                    onChange={field.onChange}
                    minutesStep={5}
                    minTime={getMinStartTime(watchDate)}
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error ? t("errors.string.empty") : undefined,
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />

              <Controller
                control={form.control}
                name="endTime"
                rules={{ required: true, validate: v => v !== null }}
                render={({ field, fieldState }) => (
                  <StyledTimePicker
                    label={t("common.ends")}
                    value={field.value}
                    onChange={field.onChange}
                    minutesStep={5}
                    minTime={watchStartTime ? add(watchStartTime, { minutes: 15 }) : undefined}
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error ? t("errors.string.empty") : undefined,
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />

              <Controller
                control={form.control}
                name="purpose"
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => (
                  <MUISelect
                    {...field}
                    label={t("messages.appointments.purpose")}
                    options={getPurposeOptions()}
                    value={field.value || ""}
                    inputRef={ref}
                    error={Boolean(form.formState.errors.purpose)}
                    formHelperText={form.formState.errors.purpose ? t("errors.field.required") : undefined}
                  />
                )}
              />

              <Controller
                control={form.control}
                name="medium"
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => (
                  <MUISelect
                    {...field}
                    label={t("messages.appointments.type")}
                    options={getMediumOptions()}
                    value={field.value || ""}
                    inputRef={ref}
                    error={Boolean(form.formState.errors.medium)}
                    formHelperText={form.formState.errors.medium ? t("errors.field.required") : undefined}
                  />
                )}
              />

              <DoubleBookingWarning
                date={watchDate}
                startTime={watchStartTime}
                endTime={watchEndTime}
                appointmentsV1={appointmentsV1}
              />
              {error && <Notification variant="danger">{t("errors.generic")}</Notification>}
              <BaseButton type="submit" text={t("messages.schedule")} disabled={postAppointmentV1.isLoading} />
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Container>
  );
};

const Container = styled.div`
  width: 400px;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 999;
  background: #fff;
  overflow-y: auto;

  ${props => props.theme.belowBreakpoint} {
    width: 100vw;
  }
`;

const StyledDateCalendar = muiStyled(DateCalendar)(({ theme }) => ({
  height: "auto",
  width: "100%",
  margin: 0,

  "& .MuiPickersCalendarHeader-root": {
    marginTop: 0,
    paddingLeft: "8px",
    paddingRight: "8px",
  },

  "& .MuiDateCalendar-viewTransitionContainer": {
    "& div[role=row]": {
      justifyContent: "space-between",
    },

    "& .MuiPickersDay-root": {
      borderColor: theme.palette.primary.main,
      "&:focus.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
}));

const StyledTimePicker = muiStyled(TimePicker)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e1e8ef",
  },

  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
}));
